import React, { useEffect, useRef, useState } from "react";
import { SERVER_URL } from "../../config/config";
import Loader from "../Loader";

export default function TransportClientsGrid({
  searchTerm,
  transportEwbEnabled,
}) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetchedRef = useRef(false);

  const [ewbLoading, setEwbLoading] = useState(false);
  const [ewbMap, setEwbMap] = useState({});

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchClients();
  }, []);

  useEffect(() => {
    if (!transportEwbEnabled) return;
    fetchTransportEWBDetails();
  }, [transportEwbEnabled]);

  const fetchClients = async () => {
    try {
      var allClients = [];
      var page = 1;
      var total = 0;

      do {
        const res = await fetch(
          SERVER_URL + "/tie_details/get_all_transporter_cleints_details",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              paginate: { number_of_rows: 100, page_number: page },
              filter_fields: {},
            }),
          },
        );

        const data = await res.json();

        if (
          data &&
          data.transport_clients &&
          Array.isArray(data.transport_clients[1])
        ) {
          total = data.transport_clients[0];
          allClients = allClients.concat(data.transport_clients[1]);
        }

        page++;
      } while (allClients.length < total);

      setClients(allClients);
    } catch (err) {
      console.error("Transport clients fetch failed", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransportEWBDetails = async () => {
    try {
      setEwbLoading(true);

      const res = await fetch(
        SERVER_URL + "/tie_details/get_transport_ewaybill_details",
        {
          method: "POST",
          headers: { Accept: "application/json" },
        },
      );

      const data = await res.json();

      if (
        data &&
        data.status === "success" &&
        Array.isArray(data.report_triggers)
      ) {
        const map = {};
        data.report_triggers.forEach((item) => {
          map[item.client_id] = item;
        });
        setEwbMap(map);
      }
    } catch (err) {
      console.error("Transport EWB fetch failed", err);
    } finally {
      setEwbLoading(false);
    }
  };

  const safeLower = function (value) {
    return typeof value === "string" ? value.toLowerCase() : "";
  };

  const display = function (value) {
    return value !== null && value !== undefined ? value : "—";
  };

  const isClientInEwbResponse = (clientId) => {
    return !!ewbMap[clientId];
  };

  const getTotalErrorCount = (ewbItem) => {
    if (!ewbItem || !Array.isArray(ewbItem.data)) return 0;
    return ewbItem.data.reduce((sum, e) => sum + (Number(e.count) || 0), 0);
  };

  const hasErrors = function (ewbItem) {
    return ewbItem && Array.isArray(ewbItem.data) && ewbItem.data.length > 0;
  };

  const filteredClients = clients.filter(function (client) {
    if (transportEwbEnabled) {
      if (!isClientInEwbResponse(client.client_id)) return false;
    }

    const search = safeLower(searchTerm);
    return (
      safeLower(client.company_name).includes(search) ||
      safeLower(client.gst_number).includes(search) ||
      safeLower(client.client_code).includes(search)
    );
  });

  const sortedClients = filteredClients.slice().sort(function (a, b) {
    if (!transportEwbEnabled) return 0;

    const aHas = hasErrors(ewbMap[a.client_id]);
    const bHas = hasErrors(ewbMap[b.client_id]);

    if (aHas && !bHas) return -1;
    if (!aHas && bHas) return 1;
    return 0;
  });

  const blockCardsWhileEwbLoading = transportEwbEnabled && ewbLoading;

  if (loading) {
    return <Loader text="Loading transport clients..." />;
  }

  return (
    <>
      {blockCardsWhileEwbLoading ? (
        <Loader text="Fetching transport EWB errors..." />
      ) : (
        <div className="company-grid">
          {sortedClients.map(function (client) {
            const ewbItem = ewbMap[client.client_id];
            const totalErrors = getTotalErrorCount(ewbItem);
            const showErrors = transportEwbEnabled && hasErrors(ewbItem);

            return (
              <div
                key={client.client_id}
                className={
                  "company-card " +
                  (showErrors
                    ? "alert-red"
                    : transportEwbEnabled
                      ? "alert-green"
                      : "")
                }
              >
                <div className="card-header">
                  <h3>{client.company_name}</h3>

                  {transportEwbEnabled && (
                    <span
                      className={
                        "health-pill " +
                        (showErrors ? "health-red" : "health-green")
                      }
                    >
                      {totalErrors}
                    </span>
                  )}
                </div>

                <div className="company-summary">
                  <div>
                    <span>Client Code</span>
                    {display(client.client_code)}
                  </div>
                  <div>
                    <span>Owner</span>
                    {display(client.owner_name)}
                  </div>
                  <div>
                    <span>GST</span>
                    {display(client.gst_number)}
                  </div>
                  <div>
                    <span>Contact</span>
                    {display(client.contact_number)}
                  </div>
                  <div>
                    <span>Email</span>
                    {display(client.email)}
                  </div>
                  <div>
                    <span>City</span>
                    {display(client.city)}
                  </div>
                  <div>
                    <span>Server</span>
                    {display(client.server_name)}
                  </div>
                  <div>
                    <span>IP</span>
                    {display(client.ip_address)}
                  </div>
                </div>

                {showErrors && (
                  <div className="usage-box">
                    <div className="usage-title error-title">Errors</div>
                    {ewbItem.data.map((err, idx) => (
                      <div key={idx} className="error-row">
                        <span>{err.error_type}</span>
                        <strong>{err.count}</strong>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
